import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Dropdown, Pagination } from 'react-bootstrap';
import { FaTrash, FaSuitcase, FaUser, FaSearch, FaWhatsapp } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight, BsSearch } from 'react-icons/bs';
import { MdOutlineSupportAgent, MdVerified, MdEmail } from 'react-icons/md';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import AdminCard from "./AdminCards"
import BaseUrl from '../../ApiServices/BaseUrl';
import Swal from 'sweetalert2';
import axios from 'axios';

import './AdminDashboard.css';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [leftSideJobs, setLeftSideJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [jobStatusCounts, setJobStatusCounts] = useState({
        new: 0,
        pending: 0,
        verified: 0
    });
    const [supportTickets, setSupportTickets] = useState([]);
    const [candidateStatusCounts, setCandidateStatusCounts] = useState({
        new: 0,
        pending: 0,
        verified: 0
    });
    const [jobSearch, setJobSearch] = useState('');
    const [candidateSearch, setCandidateSearch] = useState('');
    const [jobsPage, setJobsPage] = useState(1);
    const [candidatesPage, setCandidatesPage] = useState(1);
    const [reportedUsers, setReportedUsers] = useState([]);
    const [reportsPage, setReportsPage] = useState(1);
    const [supportPage, setSupportPage] = useState(1);
    const [shortlistCount, setShortlistCount] = useState(0);
    const itemsPerPage = 5;

    const supportScrollRef = useRef(null);
    const reportedScrollRef = useRef(null);
    const candidatesScrollRef = useRef(null);
    const jobsScrollRef = useRef(null);
    const dragStateRef = useRef({ moved: false });
    
    // Drag to scroll states
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [revealedContacts, setRevealedContacts] = useState({});

    const handleMouseDown = (e, ref) => {
        setIsDragging(true);
        dragStateRef.current.moved = false;
        setStartX(e.pageX - ref.current.offsetLeft);
        setScrollLeft(ref.current.scrollLeft);
        window.getSelection?.().removeAllRanges();
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e, ref) => {
        if (!isDragging || !ref.current) return;
        e.preventDefault();
        const x = e.pageX - ref.current.offsetLeft;
        const walk = (x - startX) * 2;
        if (Math.abs(walk) > 8) {
            dragStateRef.current.moved = true;
        }
        ref.current.scrollLeft = scrollLeft - walk;
    };

    const toggleContactVisibility = (ticketId, field) => {
        setRevealedContacts((prev) => ({
            ...prev,
            [ticketId]: {
                ...prev[ticketId],
                [field]: !prev[ticketId]?.[field]
            }
        }));
    };

    const handleSupportNameClick = (e) => {
        e.stopPropagation();
        if (dragStateRef.current.moved) {
            e.preventDefault();
            return;
        }
        navigate('/supportmessages');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const safeParseArray = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        try {
            let parsed = field;
            while (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    const getLicenseValue = (candidate) => {
        try {
            if (candidate.driving_license_country) {
                let licenseData = candidate.driving_license_country;
                if (typeof licenseData === 'string' && (licenseData.startsWith('[') || licenseData.startsWith('"'))) {
                    licenseData = JSON.parse(licenseData);
                }
                if (Array.isArray(licenseData)) {
                    const parsed = licenseData.map(l => typeof l === 'string' ? l.split('–')[0].split('-')[0].trim() : String(l));
                    const valid = parsed.filter(p => p && p !== "I Don't have License" && p !== "Not Licensed");
                    if (valid.length > 0) return valid.join(', ');
                } else if (typeof licenseData === 'string' && licenseData !== "I Don't have License") {
                    return licenseData.split('–')[0].split('-')[0].trim();
                }
            }
            if (candidate.dtr_country_doctor_license) {
                let docData = candidate.dtr_country_doctor_license;
                if (Array.isArray(docData) && docData.length > 0) return docData[0].country_name || 'Yes';
            }
            if (candidate.nurse_license_status && candidate.nurse_license_status !== "I Don't have License" && candidate.nurse_license_status !== "Not Licensed") {
                return candidate.nurse_license_status;
            }
        } catch (e) {
            console.error('Error parsing license data:', e);
        }
        return 'No';
    };

    const getCurrencyCode = (currencyValue) => {
        const value = String(currencyValue || 'USD').toLowerCase();

        if (value.includes('uae') || value.includes('united arab emirates') || value.includes('aed') || value.includes('dirham')) return 'AED';
        if (value.includes('usd') || value.includes('dollar') || value.includes('$')) return 'USD';
        if (value.includes('sar') || value.includes('saudi') || value.includes('riyal')) return 'SAR';
        if (value.includes('qar') || value.includes('qatar')) return 'QAR';
        if (value.includes('omr') || value.includes('oman')) return 'OMR';
        if (value.includes('bhd') || value.includes('bahrain')) return 'BHD';
        if (value.includes('kwd') || value.includes('kuwait')) return 'KWD';
        if (value.includes('inr') || value.includes('rupee')) return 'INR';
        if (value.includes('eur') || value.includes('euro')) return 'EUR';
        if (value.includes('gbp') || value.includes('pound')) return 'GBP';

        return String(currencyValue || 'USD').substring(0, 3).toUpperCase() || 'USD';
    };

    const formatJobSalary = (job) => {
        const minSalary = Number.parseFloat(job.minimum_monthly_salary);
        const maxSalary = Number.parseFloat(job.maximum_monthly_salary);

        if (Number.isFinite(minSalary) && minSalary > 0) {
            const currencyCode = getCurrencyCode(job.country_currency);

            if (Number.isFinite(maxSalary) && maxSalary > minSalary) {
                return `${currencyCode} ${minSalary} - ${maxSalary}/Month`;
            }

            return `${currencyCode} ${minSalary}/Month${job.negotiable ? ' (Negotiable)' : ''}`;
        }

        if (job.salary_range && String(job.salary_range).trim()) {
            return job.salary_range;
        }

        return 'Not specified';
    };

    useEffect(() => {
        const fetchShortlistCount = async () => {
            try {
                const response = await fetch(`${BaseUrl}/shortlist/all`);
                if (response.ok) {
                    const data = await response.json();
                    setShortlistCount(data.length);
                }
            } catch (err) {
                console.error('Error fetching shortlist count:', err);
            }
        };
        fetchShortlistCount();
    }, []);

    useEffect(() => {
        const fetchSupportTickets = async () => {
            try {
                const response = await fetch(`${BaseUrl}/supporttickets/all`);
                if (!response.ok) throw new Error('Failed to fetch support tickets');
                const data = await response.json();
                const sorted = data.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                setSupportTickets(sorted);
            } catch (err) {
                console.error('Error fetching support tickets:', err);
            }
        };

        const fetchReportedUsers = async () => {
            try {
                const response = await axios.get(`${BaseUrl}/api/candidatereport/`);
                const data = Array.isArray(response.data) ? response.data : [];
                const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
                setReportedUsers(sorted);
            } catch (err) {
                console.error('Error fetching reported users:', err);
            }
        };

        const fetchLeftSideJobs = async () => {
            try {
                const response = await fetch(`${BaseUrl}/api/employer/all?include_all=1`);
                if (!response.ok) throw new Error('Failed to fetch jobs');
                let data = await response.json();
                const processedData = data
                    .filter(job => job.job_title && job.job_title.trim() !== "") // Filter out incomplete/N/A jobs
                    .map(job => ({
                        ...job,
                        preferred_candidates_country: safeParseArray(job.preferred_candidates_country)
                    }));
                const sortedJobs = processedData.sort((a, b) => {
                    const dateA = new Date(a.posted_on || 0).getTime();
                    const dateB = new Date(b.posted_on || 0).getTime();
                    if (dateB !== dateA) {
                        return dateB - dateA; // Primary sort: Release Date
                    }
                    return (b.id || 0) - (a.id || 0); // Tie-breaker: Numeric ID
                });
                setLeftSideJobs(sortedJobs);
                
                setJobStatusCounts({
                    new: data.filter(job => job.status !== 'Approved').length,
                    pending: data.filter(job => job.status !== 'Approved').length,
                    verified: data.filter(job => job.status === 'Approved').length
                });
            } catch (err) {
                console.error('Error fetching jobs:', err);
            }
        };

        const fetchCandidates = async () => {
            try {
                const response = await fetch(`${BaseUrl}/api/job-seeker`);
                if (!response.ok) throw new Error('Failed to fetch candidates');
                let data = await response.json();
                const updatedData = data.map(candidate => ({
                    ...candidate,
                    profile_photo: candidate.profile_photo ? `${BaseUrl}${candidate.profile_photo}` : null,
                    job_position: safeParseArray(candidate.job_position)
                }));
                const sortedCandidates = updatedData.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                setCandidates(sortedCandidates);
                
                setCandidateStatusCounts({
                    new: data.filter(c => c.verification_status !== 'Verified').length,
                    pending: data.filter(c => c.verification_status !== 'Verified').length,
                    verified: data.filter(c => c.verification_status === 'Verified').length
                });
            } catch (err) {
                console.error('Error fetching candidates:', err);
            }
        };

        fetchSupportTickets();
        fetchReportedUsers();
        fetchLeftSideJobs();
        fetchCandidates();
    }, []);

    const openSupportTicketsCount = supportTickets.filter(t => t.status === 'open').length;

    const handleVerifyJob = async (job, e) => {
        e.stopPropagation();
        try {
            const result = await Swal.fire({
                title: 'Verify Job?',
                text: `Are you sure you want to approve "${job.job_title}"?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                confirmButtonText: 'Yes, Verify'
            });

            if (result.isConfirmed) {
                await axios.put(`${BaseUrl}/api/employer/${job.id}`, { status: 'Approved' });
                setLeftSideJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'Approved' } : j));
                Swal.fire('Verified!', 'Job has been approved.', 'success');
            }
        } catch (err) {
            Swal.fire('Error', 'Failed to verify job', 'error');
        }
    };

    const handleVerifyCandidate = async (candidate, e) => {
        e.stopPropagation();
        try {
            const result = await Swal.fire({
                title: 'Verify Candidate?',
                text: `Are you sure you want to verify ${candidate.first_name}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                confirmButtonText: 'Yes, Verify'
            });

            if (result.isConfirmed) {
                await axios.put(`${BaseUrl}/api/job-seeker/status/${candidate.user_id}`, { verification_status: 'Verified' });
                setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, verification_status: 'Verified' } : c));
                Swal.fire('Verified!', 'Candidate has been verified.', 'success');
            }
        } catch (err) {
            Swal.fire('Error', 'Failed to verify candidate', 'error');
        }
    };

    const unverifiedJobs = leftSideJobs.filter(job => 
        job.status !== 'Approved' && 
        (job.job_title?.toLowerCase().includes(jobSearch.toLowerCase()) || 
         job.domestic_worker_category?.toLowerCase().includes(jobSearch.toLowerCase()))
    );

    const unverifiedCandidates = candidates.filter(candidate => 
        candidate.verification_status !== 'Verified' && 
        (`${candidate.first_name} ${candidate.last_name}`.toLowerCase().includes(candidateSearch.toLowerCase()) || 
         candidate.job_position?.some(pos => pos.toLowerCase().includes(candidateSearch.toLowerCase())))
    );

    // Runs the existing single-verify API for each item (5 at a time) so side effects like emails still happen
    const runInBatches = async (items, fn, batchSize = 5) => {
        const succeeded = [];
        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            const results = await Promise.allSettled(batch.map(fn));
            results.forEach((r, idx) => { if (r.status === 'fulfilled') succeeded.push(batch[idx]); });
        }
        return succeeded;
    };

    const handleVerifyAll = async (type) => {
        const isJobs = type === 'jobs';
        const items = isJobs ? unverifiedJobs : unverifiedCandidates;
        if (items.length === 0) return;

        const result = await Swal.fire({
            title: isJobs ? 'Verify All Jobs?' : 'Verify All Candidates?',
            text: `This will verify ${items.length} ${isJobs ? 'job(s)' : 'candidate(s)'}${(isJobs ? jobSearch : candidateSearch) ? ' matching your search' : ''}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, Verify All'
        });
        if (!result.isConfirmed) return;

        Swal.fire({ title: 'Verifying...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        const succeeded = await runInBatches(items, item => isJobs
            ? axios.put(`${BaseUrl}/api/employer/${item.id}`, { status: 'Approved' })
            : axios.put(`${BaseUrl}/api/job-seeker/status/${item.user_id}`, { verification_status: 'Verified' })
        );
        const doneIds = new Set(succeeded.map(i => i.id));

        if (isJobs) {
            setLeftSideJobs(prev => prev.map(j => doneIds.has(j.id) ? { ...j, status: 'Approved' } : j));
            setJobStatusCounts(prev => ({ ...prev, new: prev.new - doneIds.size, pending: prev.pending - doneIds.size, verified: prev.verified + doneIds.size }));
        } else {
            setCandidates(prev => prev.map(c => doneIds.has(c.id) ? { ...c, verification_status: 'Verified' } : c));
            setCandidateStatusCounts(prev => ({ ...prev, new: prev.new - doneIds.size, pending: prev.pending - doneIds.size, verified: prev.verified + doneIds.size }));
        }

        const failed = items.length - succeeded.length;
        Swal.fire(
            failed ? 'Partially Done' : 'Verified!',
            `${succeeded.length} verified${failed ? `, ${failed} failed. Try again for the remaining.` : '.'}`,
            failed ? 'warning' : 'success'
        );
    };

    const currentJobs = unverifiedJobs.slice((jobsPage - 1) * itemsPerPage, jobsPage * itemsPerPage);
    const totalJobsPages = Math.ceil(unverifiedJobs.length / itemsPerPage);

    const currentCandidates = unverifiedCandidates.slice((candidatesPage - 1) * itemsPerPage, candidatesPage * itemsPerPage);
    const totalCandidatesPages = Math.ceil(unverifiedCandidates.length / itemsPerPage);

    const currentReports = reportedUsers.slice((reportsPage - 1) * itemsPerPage, reportsPage * itemsPerPage);
    const totalReportsPages = Math.ceil(reportedUsers.length / itemsPerPage);

    const currentSupport = supportTickets.slice((supportPage - 1) * itemsPerPage, supportPage * itemsPerPage);
    const totalSupportPages = Math.ceil(supportTickets.length / itemsPerPage);

    const renderPagination = (currentPage, totalPages, onPageChange) => {
        if (totalPages <= 1) return null;
        return (
            <div className="minimalist-pagination d-flex align-items-center gap-3">
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="p-1 px-2 border-0"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <BsChevronLeft />
                </Button>
                <div className="pagination-text-custom">
                    <span className="fw-bold text-primary">{currentPage}</span>
                    <span className="text-muted mx-1">/</span>
                    <span className="text-muted">{totalPages}</span>
                </div>
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="p-1 px-2 border-0"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    <BsChevronRight />
                </Button>
            </div>
        );
    };

    return (
        <>
            <AdminNavbar />
            <Container fluid className="p-3" style={{ marginTop: '80px' }}>
                <AdminCard />

                <Row>
                    <Col lg={12} className="mb-4">
                        <div className="dashboard-table-card">
                            <div className="table-header-custom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="table-title-custom">
                                        <FaSuitcase className="text-primary" />
                                        Unverified Jobs
                                    </h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button size="sm" variant="success" disabled={unverifiedJobs.length === 0} onClick={() => handleVerifyAll('jobs')}>
                                            <MdVerified className="me-1" />Verify All ({unverifiedJobs.length})
                                        </Button>
                                        <Badge bg="warning" text="dark">Pending: {jobStatusCounts.pending}</Badge>
                                    </div>
                                </div>
                                <div className="search-input-group">
                                    <BsSearch className="search-icon-custom" />
                                    <input 
                                        type="text" 
                                        className="search-input-custom" 
                                        placeholder="Search jobs..."
                                        value={jobSearch}
                                        onChange={(e) => setJobSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div 
                                className={`table-scroll-container ${isDragging ? 'dragging-scroll' : ''}`}
                                ref={jobsScrollRef}
                                onMouseDown={(e) => handleMouseDown(e, jobsScrollRef)}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={(e) => handleMouseMove(e, jobsScrollRef)}
                            >
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>S.No</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Job Title</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Job Type</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Location</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Salary Range</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Posted Date</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentJobs.length > 0 ? currentJobs.map((job, index) => (
                                            <tr key={job.id}>
                                                <td>{(jobsPage - 1) * itemsPerPage + index + 1}</td>
                                                <td className="dashboard-job-title-cell">
                                                    <div className="user-info-cell d-flex align-items-center">
                                                        <img src="https://cdn.helperplace.com/misc/jpi/3/Couple%20daughter%201.jpg" className="user-avatar-mini" alt="Job" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', marginRight: '8px' }} />
                                                        <span className="dashboard-job-title">{job.job_title || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{job.job_type || 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{job.working_city}, {job.state_or_province}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatJobSalary(job)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(job.posted_on)}</td>
                                                <td>
                                                    <div className="d-flex flex-nowrap gap-1">
                                                        <Button 
                                                            variant="info" 
                                                            size="sm" 
                                                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap', color: 'white' }}
                                                            onClick={() => navigate('/viewpostedjobsadmin', { state: { job } })}
                                                        >View</Button>
                                                        <Button 
                                                            variant="success" 
                                                            size="sm" 
                                                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                                            onClick={(e) => handleVerifyJob(job, e)}
                                                        >Verify</Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="7" className="text-center py-4 text-muted">No unverified jobs found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalJobsPages > 1 && (
                                <div className="p-2 d-flex justify-content-center border-top bg-light-subtle">
                                    {renderPagination(jobsPage, totalJobsPages, setJobsPage)}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12} className="mb-4">
                        <div className="dashboard-table-card">
                            <div className="table-header-custom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="table-title-custom">
                                        <FaUser className="text-primary" />
                                        Unverified Candidates
                                    </h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button size="sm" variant="success" disabled={unverifiedCandidates.length === 0} onClick={() => handleVerifyAll('candidates')}>
                                            <MdVerified className="me-1" />Verify All ({unverifiedCandidates.length})
                                        </Button>
                                        <Badge bg="warning" text="dark">Pending: {candidateStatusCounts.pending}</Badge>
                                    </div>
                                </div>
                                <div className="search-input-group">
                                    <BsSearch className="search-icon-custom" />
                                    <input 
                                        type="text" 
                                        className="search-input-custom" 
                                        placeholder="Search candidates..."
                                        value={candidateSearch}
                                        onChange={(e) => setCandidateSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div 
                                className={`table-scroll-container ${isDragging ? 'dragging-scroll' : ''}`}
                                ref={candidatesScrollRef}
                                onMouseDown={(e) => handleMouseDown(e, candidatesScrollRef)}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={(e) => handleMouseMove(e, candidatesScrollRef)}
                            >
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>S.No</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Name</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Category</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Position</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>License</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Age/Gender</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Nationality</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Registration Date</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Publishing</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Add-ons</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Open</th>
                                        </tr>
                                    </thead>
                                    <tbody className="unverified-candidates-tbody">
                                        {currentCandidates.length > 0 ? currentCandidates.map((candidate, index) => (
                                            <tr key={candidate.id}>
                                                <td>{(candidatesPage - 1) * itemsPerPage + index + 1}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <div className="user-info-cell d-flex align-items-center">
                                                        <img src={candidate.profile_photo || "https://cdn.helperplace.com/misc/jpi/3/Couple%20daughter%201.jpg"} className="user-avatar-mini" alt={candidate.first_name} style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', marginRight: '8px' }} />
                                                        <span>{candidate.first_name} {candidate.last_name}</span>
                                                    </div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{candidate.category || 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{candidate.job_position ? (Array.isArray(candidate.job_position) ? candidate.job_position.join(', ') : candidate.job_position) : 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{getLicenseValue(candidate)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{candidate.age ? `${candidate.age} Yrs` : 'N/A'} / {candidate.gender || 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{candidate.nationality || 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(candidate.created_at)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <Badge bg="secondary">Unpublished</Badge>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>—</td>
                                                <td>
                                                    <div className="d-flex flex-nowrap gap-1">
                                                        <Button 
                                                            variant="info" 
                                                            size="sm" 
                                                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap', color: 'white' }}
                                                            onClick={() => navigate(`/candidatesviewadmin/${candidate.user_id}`)}
                                                        >View Details</Button>
                                                        <Button 
                                                            variant="primary" 
                                                            size="sm" 
                                                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                                            onClick={() => navigate('/adminpersonalinfo', {
                                                                state: {
                                                                    userId: candidate.user_id,
                                                                    candidateData: candidate
                                                                }
                                                            })}
                                                        >Edit</Button>
                                                        <Button 
                                                            variant="success" 
                                                            size="sm" 
                                                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                                            onClick={(e) => handleVerifyCandidate(candidate, e)}
                                                        >Verify</Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="11" className="text-center py-4 text-muted">No unverified candidates found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalCandidatesPages > 1 && (
                                <div className="p-2 d-flex justify-content-center border-top bg-light-subtle">
                                    {renderPagination(candidatesPage, totalCandidatesPages, setCandidatesPage)}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12} className="mb-4">
                        <div className="dashboard-table-card">
                            <div className="table-header-custom">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <h5 className="table-title-custom"><MdOutlineSupportAgent className="text-primary" /> Support Messages</h5>
                                    <Badge bg="primary" text="white">Open: {openSupportTicketsCount}</Badge>
                                </div>
                            </div>
                            <div 
                                className={`table-scroll-container ${isDragging ? 'dragging-scroll' : ''}`}
                                ref={supportScrollRef}
                                onMouseDown={(e) => handleMouseDown(e, supportScrollRef)}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={(e) => handleMouseMove(e, supportScrollRef)}
                            >
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>S.No</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Name</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Number</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Email</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>User Role</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Job Category</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Nationality</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Open</th>
                                        </tr>
                                    </thead>
                                    <tbody className="support-messages-tbody">
                                        {currentSupport.length > 0 ? currentSupport.map((ticket, index) => (
                                            <tr key={ticket.id}>
                                                <td>{(supportPage - 1) * itemsPerPage + index + 1}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <button
                                                        type="button"
                                                        className="support-name-button"
                                                        onClick={handleSupportNameClick}
                                                    >
                                                        {ticket.name}
                                                    </button>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {ticket.mobile_number ? (
                                                        <div className="support-contact-cell">
                                                            <button
                                                                type="button"
                                                                className="support-contact-icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleContactVisibility(ticket.id, 'mobile');
                                                                }}
                                                                aria-label={revealedContacts[ticket.id]?.mobile ? 'Hide WhatsApp number' : 'Show WhatsApp number'}
                                                            >
                                                                <FaWhatsapp />
                                                            </button>
                                                            {revealedContacts[ticket.id]?.mobile && (
                                                                <span className="support-contact-value">{ticket.mobile_number}</span>
                                                            )}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td>
                                                    {ticket.email ? (
                                                        <div className="support-contact-cell">
                                                            <button
                                                                type="button"
                                                                className="support-contact-icon support-contact-icon-email"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleContactVisibility(ticket.id, 'email');
                                                                }}
                                                                aria-label={revealedContacts[ticket.id]?.email ? 'Hide email address' : 'Show email address'}
                                                            >
                                                                <MdEmail />
                                                            </button>
                                                            {revealedContacts[ticket.id]?.email && (
                                                                <span className="support-contact-value">{ticket.email}</span>
                                                            )}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{ticket.user_role ? ticket.user_role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{ticket.job_category || <span className="text-muted">—</span>}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{ticket.nationality || <span className="text-muted">—</span>}</td>
                                                <td>
                                                    <Badge bg={ticket.status === 'open' ? 'warning' : ticket.status === 'closed' ? 'secondary' : 'info'} text={ticket.status === 'open' ? 'dark' : 'white'}>
                                                        {ticket.status === 'open' ? 'Open' : ticket.status === 'closed' ? 'Closed' : ticket.status}
                                                    </Badge>
                                                </td>
                                                <td><span className="support-open-hint">Click name</span></td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="9" className="text-center py-4 text-muted">No messages found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalSupportPages > 1 && (
                                <div className="p-2 d-flex justify-content-center border-top bg-light-subtle">
                                    {renderPagination(supportPage, totalSupportPages, setSupportPage)}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12} className="mb-4">
                        <div className="dashboard-table-card">
                            <div className="table-header-custom">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <h5 className="table-title-custom"><FaTrash className="text-danger" /> Reported Users</h5>
                                    <Badge bg="danger" text="white">Total: {reportedUsers.length}</Badge>
                                </div>
                            </div>
                            <div 
                                className={`table-scroll-container ${isDragging ? 'dragging-scroll' : ''}`}
                                ref={reportedScrollRef}
                                onMouseDown={(e) => handleMouseDown(e, reportedScrollRef)}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={(e) => handleMouseMove(e, reportedScrollRef)}
                            >
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th>Reporter</th>
                                            <th>Reported Account</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReports.length > 0 ? currentReports.map(report => (
                                            <tr key={report.id}>
                                                <td>
                                                    <span className="user-name-mini">{report.first_name}</span>
                                                    <span className="user-subinfo-mini">ID: {report.emp_id}</span>
                                                </td>
                                                <td>
                                                    <span className="user-name-mini">{report.candidate_name}</span>
                                                    <span className="user-subinfo-mini">{report.email}</span>
                                                </td>
                                                <td><Badge bg="light" text="dark" className="border">{report.reason}</Badge></td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="3" className="text-center py-4 text-muted">No reports found</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalReportsPages > 1 && (
                                <div className="p-2 d-flex justify-content-center border-top bg-light-subtle">
                                    {renderPagination(reportsPage, totalReportsPages, setReportsPage)}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12} className="mb-4">
                        <Card className="mb-4 shadow-sm text-center p-4">
                            <p className="mb-3 fw-bold">Find the all posted Jobs and candidates.</p>
                            <div className="d-flex flex-column flex-sm-row justify-content-center flex-wrap gap-3">
                                <Button variant="success" style={{ minWidth: '180px' }} onClick={() => navigate('/postedjobsadmin')}>FIND JOBS</Button>
                                <Button variant="warning" className="text-white" style={{ minWidth: '180px' }} onClick={() => navigate('/candidatesadmin')}>FIND CANDIDATES</Button>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default AdminDashboard;
